import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wand2, Trophy, AlertTriangle, Coins, Search } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["General", "Fuel", "Groceries", "Dining", "Travel", "Hotels", "Utilities", "Online", "Insurance", "International"];

function Recommender() {
    const [form, setForm] = useState({ merchant: "", category: "General", country: "IN", currency: "INR", amount: 5000 });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/card-advisor/recommend", { ...form, amount: Number(form.amount) });
            setResult(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    return (
        <div>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-white border border-slate-200 rounded-lg p-4 mb-4">
                <div><Label className="eyebrow">Merchant</Label><Input value={form.merchant} onChange={(e) => setForm((f) => ({ ...f, merchant: e.target.value }))} placeholder="e.g. Zomato" data-testid="rec-merchant" /></div>
                <div>
                    <Label className="eyebrow">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                        <SelectTrigger data-testid="rec-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div><Label className="eyebrow">Country</Label><Input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} placeholder="IN" /></div>
                <div><Label className="eyebrow">Currency</Label><Input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} placeholder="INR" /></div>
                <div><Label className="eyebrow">Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required data-testid="rec-amount" /></div>
                <div className="md:col-span-5">
                    <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="rec-submit">
                        <Search size={14} className="mr-1.5" /> {busy ? "Scoring…" : "Recommend card"}
                    </Button>
                </div>
            </form>

            {result && (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <table className="dense-table w-full">
                        <thead><tr>
                            <th></th><th>Card</th>
                            <th className="text-right">Reward rate</th>
                            <th className="text-right">Rewards</th>
                            <th className="text-right">Offer</th>
                            <th className="text-right">Forex ₹</th>
                            <th className="text-right">Lounge</th>
                            <th className="text-right">Score</th>
                            <th>Why</th>
                        </tr></thead>
                        <tbody>
                            {result.recommendations.map((r, i) => (
                                <tr key={r.card_id} className={i === 0 ? "bg-emerald-50" : ""} data-testid={`rec-row-${r.card_id}`}>
                                    <td>{i === 0 ? <Trophy size={14} className="text-emerald-700" /> : <span className="text-stone-400 text-xs tabular-nums">{i+1}</span>}</td>
                                    <td>
                                        <Link to={`/cards/${r.card_id}/view`} className="text-stone-900 font-medium hover:underline">{r.bank_name}</Link>
                                        <div className="text-xs text-stone-500">{r.card_name} · ••{r.last_4}</div>
                                    </td>
                                    <td className="text-right tabular-nums">{r.reward_rate_pct}%</td>
                                    <td className="text-right tabular-nums">{fmtMoney(r.rewards)}</td>
                                    <td className="text-right tabular-nums text-blue-700">{r.offer_bonus ? fmtMoney(r.offer_bonus) : "—"}</td>
                                    <td className="text-right tabular-nums">{r.forex_saving ? fmtMoney(r.forex_saving) : "—"}</td>
                                    <td className="text-right tabular-nums">{r.lounge_bonus ? fmtMoney(r.lounge_bonus) : "—"}</td>
                                    <td className={`text-right tabular-nums font-medium ${i === 0 ? "text-emerald-700" : ""}`}>{fmtMoney(r.score)}</td>
                                    <td className="text-xs text-stone-500">{r.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function MissedBenefit() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(90);
    useEffect(() => { api.get("/card-advisor/missed-benefit", { params: { days } }).then((r) => setData(r.data)); }, [days]);

    if (!data) return <div className="text-stone-500 py-10 text-center">Loading…</div>;
    return (
        <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="kpi-card"><div className="eyebrow">Analyzed</div><div className="font-display text-2xl mt-2 tabular-nums">{data.total_expenses_analyzed}</div></div>
                <div className="kpi-card"><div className="eyebrow">Flagged</div><div className="font-display text-2xl mt-2 tabular-nums text-amber-700">{data.flagged_rows}</div></div>
                <div className="kpi-card"><div className="eyebrow">Total missed (₹)</div><div className="font-display text-2xl mt-2 tabular-nums text-red-700">{fmtMoney(data.total_missed_rupees)}</div></div>
                <div className="kpi-card"><div className="eyebrow">Window</div>
                    <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                        <SelectTrigger className="mt-2 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{[30, 60, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="dense-table w-full">
                    <thead><tr><th>Date</th><th>Merchant</th><th>Category</th><th className="text-right">Amount</th><th>Used</th><th>Best</th><th className="text-right">Missed ₹</th></tr></thead>
                    <tbody>
                        {data.rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-emerald-700">🎯 No missed benefits detected — you are optimizing well.</td></tr>}
                        {data.rows.map((r) => (
                            <tr key={r.expense_id} data-testid={`missed-row-${r.expense_id}`}>
                                <td className="tabular-nums text-xs">{fmtDate(r.date)}</td>
                                <td className="text-sm">{r.merchant}</td>
                                <td className="text-xs text-stone-500">{r.category}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.amount)}</td>
                                <td className="text-xs">
                                    <div className="text-stone-900">{r.used_card_label}</div>
                                    <div className="text-[10.5px] text-stone-500 tabular-nums">earned {fmtMoney(r.used_rewards)}</div>
                                </td>
                                <td className="text-xs">
                                    <div className="text-emerald-700 font-medium">{r.best_card_label}</div>
                                    <div className="text-[10.5px] text-stone-500 tabular-nums">could earn {fmtMoney(r.best_rewards)}</div>
                                </td>
                                <td className="text-right tabular-nums font-medium text-red-700">{fmtMoney(r.missed_amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function RewardsLedger() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/card-advisor/rewards-ledger").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-stone-500 py-10 text-center">Loading…</div>;
    return (
        <div>
            <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="kpi-card"><div className="eyebrow">Cashback earned FY</div><div className="font-display text-2xl mt-2 tabular-nums text-emerald-700">{fmtMoney(data.totals?.cashback_earned || 0)}</div></div>
                <div className="kpi-card"><div className="eyebrow">Points earned</div><div className="font-display text-2xl mt-2 tabular-nums">{Number(data.totals?.points_earned || 0).toLocaleString()}</div></div>
                <div className="kpi-card"><div className="eyebrow">Points redeemed</div><div className="font-display text-2xl mt-2 tabular-nums text-amber-700">{Number(data.totals?.points_redeemed || 0).toLocaleString()}</div></div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="dense-table w-full">
                    <thead><tr><th>Card</th><th className="text-right">Cashback</th><th className="text-right">Pts earned</th><th className="text-right">Pts redeemed</th><th className="text-right">Balance on card</th><th className="text-right">₹/pt</th><th className="text-right">Net value</th><th className="text-right">Total benefit</th></tr></thead>
                    <tbody>
                        {data.rows.map((r) => (
                            <tr key={r.card_id} data-testid={`ledger-row-${r.card_id}`}>
                                <td>
                                    <Link to={`/cards/${r.card_id}/view`} className="text-stone-900 font-medium hover:underline">{r.bank_name}</Link>
                                    <div className="text-xs text-stone-500">{r.card_name} · ••{r.last_4}</div>
                                </td>
                                <td className="text-right tabular-nums">{fmtMoney(r.cashback_earned)}</td>
                                <td className="text-right tabular-nums">{r.points_earned.toLocaleString()}</td>
                                <td className="text-right tabular-nums text-amber-700">{r.points_redeemed.toLocaleString()}</td>
                                <td className="text-right tabular-nums text-stone-500">{r.points_balance_on_card.toLocaleString()}</td>
                                <td className="text-right tabular-nums">₹{r.point_value}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.net_value)}</td>
                                <td className="text-right tabular-nums font-medium">{fmtMoney(r.total_benefit)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function CardAdvisor() {
    const [tab, setTab] = useState("recommend");
    return (
        <div data-testid="card-advisor-page">
            <div className="mb-6">
                <div className="eyebrow flex items-center gap-1.5"><Wand2 size={11} /> Card Advisor · Intelligent recommendations</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Right card. Every time.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Recommend the best card for any transaction, spot cases where a different card
                    would have earned more, and see your rewards ledger across every card.
                </p>
            </div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="recommend" data-testid="tab-recommend"><Trophy size={12} className="mr-1.5" /> Recommend</TabsTrigger>
                    <TabsTrigger value="missed" data-testid="tab-missed"><AlertTriangle size={12} className="mr-1.5" /> Missed Benefit</TabsTrigger>
                    <TabsTrigger value="ledger" data-testid="tab-ledger"><Coins size={12} className="mr-1.5" /> Rewards Ledger</TabsTrigger>
                </TabsList>
                <TabsContent value="recommend"><Recommender /></TabsContent>
                <TabsContent value="missed"><MissedBenefit /></TabsContent>
                <TabsContent value="ledger"><RewardsLedger /></TabsContent>
            </Tabs>
        </div>
    );
}
